import type { InstagramPartnerPermissionStatusWire } from "@common/interfaces/adsManagerGuidedEdit";
import { resolvePartnershipSelectBadge } from "@common/utils/partnershipPartnerOptions";
import type { FacebookPage, InstagramAccount } from "../types";

function withPartnershipSelectBadgeFields(page: FacebookPage): FacebookPage {
  return {
    ...page,
    ...resolvePartnershipSelectBadge({
      partnership_permission_status:
        (page.partnership_permission_status ?? "UNKNOWN") as InstagramPartnerPermissionStatusWire,
      partnership_status_label: page.partnership_status_label ?? "",
      partnership_has_permission_row: page.partnership_has_permission_row ?? false,
      partnership_permission_url: page.partnership_permission_url,
      partnership_permission_id: page.partnership_permission_id,
      is_eligible_for_branded_content: page.is_eligible_for_branded_content,
      is_ad_account_page: page.is_ad_account_page,
    }),
  };
}

function withPartnershipSelectBadgeFieldsForInstagram(
  account: InstagramAccount
): InstagramAccount {
  return {
    ...account,
    ...resolvePartnershipSelectBadge({
      partnership_permission_status:
        (account.partnership_permission_status ??
          "UNKNOWN") as InstagramPartnerPermissionStatusWire,
      partnership_status_label: account.partnership_status_label ?? "",
      partnership_has_permission_row:
        account.partnership_has_permission_row ?? false,
      partnership_permission_url: account.partnership_permission_url,
      partnership_permission_id: account.partnership_permission_id,
      is_ad_account_page: account.is_ad_account_page,
    }),
  };
}

/** Mirrors GET /ads/partnership_partner_options (backend splits advertiser vs partner catalog). */
export type PartnershipPartnerOptionsApiResponse = {
  advertiser_facebook_pages?: FacebookPage[];
  partner_facebook_pages?: FacebookPage[];
  advertiser_instagram_accounts?: InstagramAccount[];
  partner_instagram_accounts?: InstagramAccount[];
  /** @deprecated Prefer `partner_facebook_pages`. */
  facebook_pages?: FacebookPage[];
  /** @deprecated Prefer `partner_instagram_accounts`. */
  instagram_accounts?: InstagramAccount[];
  advertiser_identity: {
    page_id?: string;
    instagram_user_id?: string;
    business_id?: string;
    validation_messages: string[];
    partnership_permissions_loaded?: boolean;
    partnership_permissions_count?: number;
  };
};

/** UI-ready slices from one partnership_partner_options response (single fetch). */
export type PartnershipIdentityCatalog = {
  advertiserFacebookPages: FacebookPage[];
  partnerFacebookPages: FacebookPage[];
  /** Brand-owned IG rows for the advertiser identity picker. */
  instagramAccounts: InstagramAccount[];
  /** Full IG superset for the partnership partner picker. */
  partnerInstagramAccounts: InstagramAccount[];
  advertiserValidationMessages: string[];
  partnershipPermissionsLoaded: boolean;
  metaBusinessId?: string;
};

/**
 * @deprecated Backend returns `advertiser_facebook_pages`; kept for tests and legacy payloads.
 */
export function filterPagesForAdvertiserIdentity(
  pages: readonly FacebookPage[]
): FacebookPage[] {
  return pages.filter((page) => page.is_ad_account_page !== false);
}

export function filterInstagramForAdvertiserIdentity(
  accounts: readonly InstagramAccount[]
): InstagramAccount[] {
  return accounts.filter((account) => account.is_ad_account_page !== false);
}

/**
 * Partner Facebook picker: union `/ads/pages` (me/accounts) with partnership
 * catalog rows so every owned page is selectable for permission requests.
 */
export function mergePartnerFacebookPagesForPicker(
  identityPages: readonly FacebookPage[],
  catalogPartnerPages: readonly FacebookPage[]
): FacebookPage[] {
  const catalogById = new Map(
    catalogPartnerPages.map((page) => [page.id, page] as const)
  );
  const merged: FacebookPage[] = [];
  const seen = new Set<string>();

  for (const page of identityPages) {
    const id = page.id?.trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const catalog = catalogById.get(id);
    merged.push(
      catalog
        ? {
            ...page,
            ...catalog,
            name: catalog.name || page.name,
            picture: catalog.picture || page.picture,
          }
        : withPartnershipSelectBadgeFields({
            ...page,
            is_ad_account_page: false,
            partnership_permission_status:
              page.partnership_permission_status ?? "UNKNOWN",
            partnership_status_label: page.partnership_status_label ?? "",
            partnership_has_permission_row:
              page.partnership_has_permission_row ?? false,
          })
    );
  }

  for (const catalog of catalogPartnerPages) {
    if (!seen.has(catalog.id)) {
      merged.push(catalog);
      seen.add(catalog.id);
    }
  }

  return merged.sort((a, b) => a.name.localeCompare(b.name));
}

export function mapPartnershipPartnerOptionsResponse(
  data: PartnershipPartnerOptionsApiResponse
): PartnershipIdentityCatalog {
  const partnerFacebookPages = (
    data.partner_facebook_pages ??
    data.facebook_pages ??
    []
  ).map(withPartnershipSelectBadgeFields);
  const partnerInstagramAccounts = (
    data.partner_instagram_accounts ??
    data.instagram_accounts ??
    []
  ).map(withPartnershipSelectBadgeFieldsForInstagram);

  const advertiserFacebookPages = (
    data.advertiser_facebook_pages ??
    filterPagesForAdvertiserIdentity(partnerFacebookPages)
  ).map(withPartnershipSelectBadgeFields);
  const advertiserInstagramAccounts = (
    data.advertiser_instagram_accounts ??
    filterInstagramForAdvertiserIdentity(partnerInstagramAccounts)
  ).map(withPartnershipSelectBadgeFieldsForInstagram);

  return {
    partnerFacebookPages,
    advertiserFacebookPages,
    instagramAccounts: advertiserInstagramAccounts,
    partnerInstagramAccounts,
    advertiserValidationMessages:
      data.advertiser_identity?.validation_messages ?? [],
    partnershipPermissionsLoaded: Boolean(
      data.advertiser_identity?.partnership_permissions_loaded
    ),
    metaBusinessId: data.advertiser_identity?.business_id?.trim() || undefined,
  };
}
